import * as React from "react";
import { FC, useCallback } from "react";
import * as _ from "lodash";
import classNames from "classnames";
import { FileWithPreview } from "react-dropzone";
import { format } from "lib/globalize-util";
import { themeOptions } from "styles/theme";
import { FormatMessage } from "react-globalize";
import Grid from "@material-ui/core/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import FilesIcon from "components/icons/files";
import UploadedFilesIcon from "components/icons/uploaded-files";
import Reminders from "components/product-media/components/reminders";
import Uploader, { IMediaUploaderProps } from "components/product-media/components/uploader";
import { DATATEST } from "constants/integration";

const MAX_SIZE = 5; // MB

// prettier-ignore
export interface IProps extends Omit<IMediaUploaderProps, "accept"> {
    success?: boolean;
    onUpload (results: Array<[any, FileWithPreview]>): void;
}

export const Uploader360: FC<IProps> = ({ success, onUpload, ...rest }) => {
    // prettier-ignore
    const handleUpload = useCallback(
        (results: Array<[any, FileWithPreview]>) => onUpload(_.sortBy(results, ([, file]) => _.toLower(file.name))),
        [onUpload]);
    const classes = useStyles({});
    return (
        <Grid container={true} wrap="wrap" justify="space-around" spacing={2}>
            <Grid className={classes.margin} item={true}>
                {/*prettier-ignore*/}
                <Uploader
                    inputProps={{
                        "data-test": DATATEST.MEDIA_DIALOG.IMAGES_360_UPLOADER,
                    }}
                    multiple={true}
                    accept="image/jpeg,image/png"
                    maxSize={MAX_SIZE * 1024 * 1024}
                    onUpload={handleUpload}
                    minFiles={4}
                    maxFiles={72}
                    {...rest}
                >
                    {success
                        ? <UploadedFilesIcon className={classNames(classes.icon, classes.iconSuccess)}/>
                        : <FilesIcon className={classNames(classes.icon, classes.iconFiles)}/>
                    }
                    <div className={classes.dragLabel}>
                        <FormatMessage
                            path="Drag your image files here |br| or |span|click here|span| to select"
                            elements={{ br: <br/>, span: <span className={classes.click}/> }}
                        />
                    </div>
                </Uploader>
            </Grid>
            <Grid
                className={classes.margin}
                data-test={DATATEST.MEDIA_DIALOG.IMAGES_360_SPECIFICATIONS}
                item={true}
                xs={true}
            >
                <Reminders title={format("Image specifications:")}>
                    <li>{format("JPG_PNG only")}</li>
                    <li>
                        {format(
                            "Image naming: will be displayed in sequential order and must include numbering (styleID_black_360_01.jpg)",
                        )}
                    </li>
                    <li>{format("Image size: 1400px x 2200px recommended")}</li>
                    <li>
                        {format("Image count: |mid| images recommended, minimum of |min|, maximum of |max| images", {
                            min: 4,
                            mid: 24,
                            max: 72,
                        })}
                    </li>
                    <li>
                        {format("File size: |min| - |recommended| recommended, |max| max", {
                            min: "500 KB",
                            max: `${MAX_SIZE} MB`,
                            recommended: "1.5 MB",
                        })}
                    </li>
                </Reminders>
            </Grid>
        </Grid>
    );
};

const useStyles = makeStyles({
    click: {
        textDecoration: "underline",
    },
    icon: {
        width: 160,
        height: 160,
        padding: 30,
        margin: 10,
    },
    iconFiles: {
        color: themeOptions.palette.grey,
    },
    iconSuccess: {
        color: themeOptions.palette.accent1Color,
    },
    dragLabel: {
        fontSize: 14,
        textAlign: "center",
        lineHeight: "1.8em",
        color: themeOptions.palette.darkGrey,
    },
    margin: {
        margin: 20,
        ["@media(max-width: 600px)"]: {
            margin: 10,
        },
    },
});

export default Uploader360;
